import { phaseRule } from './rules/phaseRule'

/**
 * Splits an assistant response into typed blocks, leaving everything a rule
 * does not recognise as `markdown` for the Stage 2 renderer.
 *
 * Deliberately a *pre-pass*, not a replacement: rewriting the whole response
 * into blocks would mean re-implementing markdown inside every block type and
 * would throw away Stage 2's table, heading and citation correctness. Segments
 * tile the input with no gaps or overlaps — asserted by the span test — so text
 * can never be dropped or duplicated by inference.
 *
 * Bump `RULES_VERSION` on any rule change; it is part of the memo key, and a
 * stale memo would otherwise survive a rule edit within a session.
 */
export const RULES_VERSION = 1

const RULES = [phaseRule]

/** @returns {Array<{ type: string, span: [number, number] }>} */
export function inferResponseBlocks(text) {
  const source = String(text ?? '')
  if (!source.trim()) return []

  const lines = source.split('\n')
  const blocks = []
  let pending = []
  let pendingStart = 0

  const flush = (end) => {
    if (!pending.length) return
    const value = pending.join('\n')
    if (value.trim()) blocks.push({ type: 'markdown', text: value, span: [pendingStart, end] })
    pending = []
  }

  for (let i = 0; i < lines.length; ) {
    const match = RULES.reduce((found, rule) => found ?? rule(lines, i), null)
    if (match) {
      flush(i)
      blocks.push({ ...match.block, span: [i, i + match.consumed] })
      i += match.consumed
      pendingStart = i
      continue
    }
    if (!pending.length) pendingStart = i
    pending.push(lines[i])
    i += 1
  }
  flush(lines.length)

  return blocks
}
